// src/features/banking/pages/BankAccountDetailPage.tsx
// MERGED TRANSACTIONS FROM BOTH TABLES

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    ArrowLeft, CreditCard, PiggyBank, Landmark, Wallet,
    ArrowUpRight, ArrowDownLeft, TrendingUp, TrendingDown,
    Calendar, Activity, Target, ChevronLeft, ChevronRight,
    History, Search, X, Store, Building2,
    Award, Banknote, Smartphone, Receipt, RefreshCw,
    ExternalLink,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import apiClient from '@/lib/api-client';

// ============================================================
// TYPES
// ============================================================

interface BankAccountData {
    id: string;
    bankName: string;
    accountNumber: string;
    accountName: string;
    accountType: string;
    currency: string;
    currentBalance: string;
    openingBalance: string;
    isActive: boolean;
    isDefault: boolean;
    branchId: string | null;
    businessId: string | null;
    branchName?: string;
    businessName?: string;
    createdAt: string;
    updatedAt: string;
}

// Unified transaction type
interface UnifiedTransaction {
    id: string;
    type: 'credit' | 'debit';
    amount: string;
    balanceBefore?: string;
    balanceAfter?: string;
    source: string;
    description: string | null;
    reference: string | null;
    channel: string | null;
    saleId: string | null;
    saleOrderNumber: string | null;  // Allow null
    status: string | null;
    createdAt: string;
    sourceTable: 'banking' | 'sales';
}


// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '₦0.00';
    if (Math.abs(num) >= 1000000) return `₦${(num / 1000000).toFixed(2)}M`;
    if (Math.abs(num) >= 1000) return `₦${(num / 1000).toFixed(1)}K`;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
};

const formatDateTime = (date: string) => {
    try {
        return new Date(date).toLocaleString('en-NG', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    } catch { return 'N/A'; }
};

const formatTimeAgo = (date: string) => {
    try {
        const now = new Date();
        const then = new Date(date);
        const diffMs = now.getTime() - then.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return 'N/A'; }
};

const getSourceIcon = (source: string) => {
    switch (source?.toLowerCase()) {
        case 'bank_transfer': return Building2;
        case 'pos_card': return CreditCard;
        case 'mobile_money': return Smartphone;
        case 'cash': return Banknote;
        case 'sale': return Receipt;
        case 'expense': return ArrowDownLeft;
        case 'transfer': return RefreshCw;
        case 'opening_balance': return Wallet;
        case 'adjustment': return Activity;
        default: return Receipt;
    }
};

const getSourceLabel = (source: string) => {
    if (!source) return 'Transaction';
    return source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// ============================================================
// ACCOUNT TYPE CONFIG
// ============================================================

const accountTypeConfig: Record<string, {
    label: string; icon: React.ElementType; gradient: string; bgLight: string;
    textColor: string; heroGradient: string;
}> = {
    savings: {
        label: 'Savings', icon: PiggyBank,
        gradient: 'from-emerald-500 to-teal-600', bgLight: 'bg-emerald-50',
        textColor: 'text-emerald-600', heroGradient: 'from-emerald-700 via-teal-700 to-emerald-800'
    },
    current: {
        label: 'Current', icon: Landmark,
        gradient: 'from-blue-500 to-indigo-600', bgLight: 'bg-blue-50',
        textColor: 'text-blue-600', heroGradient: 'from-blue-700 via-indigo-700 to-blue-800'
    },
    cash: {
        label: 'Cash', icon: Wallet,
        gradient: 'from-amber-500 to-orange-600', bgLight: 'bg-amber-50',
        textColor: 'text-amber-600', heroGradient: 'from-amber-600 via-orange-600 to-amber-700'
    },
    other: {
        label: 'Other', icon: CreditCard,
        gradient: 'from-purple-500 to-violet-600', bgLight: 'bg-purple-50',
        textColor: 'text-purple-600', heroGradient: 'from-purple-700 via-violet-700 to-purple-800'
    },
};

const filterOptions = [
    { value: '', label: 'All' },
    { value: 'sale', label: 'Sales' },
    { value: 'expense', label: 'Expenses' },
    { value: 'transfer', label: 'Transfers' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'pos_card', label: 'Card' },
    { value: 'cash', label: 'Cash' },
    { value: 'mobile_money', label: 'MoMo' },
    { value: 'opening_balance', label: 'Opening Bal' },
    { value: 'adjustment', label: 'Adjustments' },
];

// ============================================================
// STAT CARD COMPONENT
// ============================================================

function StatCard({ label, value, icon: Icon, gradient, trend }: {
    label: string; value: string | number; icon: React.ElementType; gradient: string; trend?: 'up' | 'down';
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -3 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between mb-3">
                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} shadow-md`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
                {trend && (
                    <span className={clsx('text-xs font-bold flex items-center gap-0.5',
                        trend === 'up' ? 'text-emerald-500' : 'text-rose-500')}>
                        {trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownLeft size={12} />}
                    </span>
                )}
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
            <p className="text-xl font-bold text-slate-900 mt-1">
                {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
        </motion.div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function BankAccountDetailPage() {
    const { accountId } = useParams<{ accountId: string }>();
    const navigate = useNavigate();
    const tenantId = useAuthStore(s => s.user?.tenantId);

    const [searchQuery, setSearchQuery] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 30;

    // ✅ Fetch bank account from banking module
    const { data: account, isLoading: accountLoading, isError: accountError } = useQuery({
        queryKey: ['bank-account', accountId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/bank-accounts/${accountId}`);
            return (data?.data || data) as BankAccountData;
        },
        enabled: !!accountId,
    });

    // ✅ Fetch transactions from banking module (account_transactions table)
    const { data: bankingTxData, isLoading: bankingTxLoading } = useQuery({
        queryKey: ['banking-transactions', accountId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/bank-accounts/${accountId}/transactions`, {
                params: { limit: 200, page: 1 }
            });
            const responseData = data?.data || data;
            const transactions = responseData?.transactions || [];
            console.log('🏦 Banking transactions:', {
                count: transactions.length,
                raw: responseData
            });
            return (Array.isArray(transactions) ? transactions : []).map((tx: any): UnifiedTransaction => ({
                id: tx.id,
                type: tx.type as 'credit' | 'debit',
                amount: tx.amount,
                balanceBefore: tx.balanceBefore,
                balanceAfter: tx.balanceAfter,
                source: tx.source || 'unknown',
                description: tx.description || null,
                reference: tx.referenceType || null,
                channel: null,
                saleId: tx.referenceType === 'sale_order' ? tx.referenceId : null,
                saleOrderNumber: null,
                status: 'confirmed',
                createdAt: tx.createdAt,
                sourceTable: 'banking' as const,
            }));
        },
        enabled: !!accountId,
    });


    // ✅ Fetch transactions from sales module (bank_transactions table)
    // Replace the salesTxData useQuery with this debug version:
    const { data: salesTxData, isLoading: salesTxLoading } = useQuery({
        queryKey: ['sales-bank-transactions', accountId],
        queryFn: async () => {
            try {
                console.log('🔍 Fetching sales for bank account:', accountId);

                const { data } = await apiClient.get(`/tenants/${tenantId}/sales`, {
                    params: { limit: 50, sortBy: 'createdAt', order: 'desc' }
                });

                const sales = data?.data?.sales || data?.data || [];
                console.log('📊 Sales response:', {
                    totalSales: sales.length,
                    rawData: data?.data,
                    hasSalesKey: !!data?.data?.sales
                });

                const transactions: UnifiedTransaction[] = [];

                for (const sale of sales) {
                    console.log('📋 Sale:', {
                        id: sale.id,
                        orderNumber: sale.orderNumber,
                        payments: sale.payments?.length || 0,
                        paymentDetails: sale.payments?.map((p: any) => ({
                            bankAccountId: p.bankAccountId,
                            method: p.paymentMethod,
                            amount: p.amount
                        }))
                    });

                    if (sale.payments) {
                        for (const payment of sale.payments) {
                            if (payment.bankAccountId === accountId) {
                                console.log('✅ Found matching payment:', payment);
                                transactions.push({
                                    id: payment.id,
                                    type: 'credit' as const,
                                    amount: payment.amount,
                                    balanceBefore: undefined,
                                    balanceAfter: undefined,
                                    source: payment.paymentMethod || 'sale',
                                    description: `Sale ${sale.orderNumber}`,
                                    reference: payment.reference || null,
                                    channel: payment.paymentMethod || null,
                                    saleId: sale.id,
                                    saleOrderNumber: sale.orderNumber || null,
                                    status: payment.status || 'confirmed',
                                    createdAt: payment.createdAt || sale.createdAt,
                                    sourceTable: 'sales' as const,
                                });
                            }
                        }
                    }
                }

                console.log('💰 Total sales transactions found:', transactions.length);
                return transactions;
            } catch (err) {
                console.error('❌ Failed to fetch sales:', err);
                return [] as UnifiedTransaction[];
            }
        },
        enabled: !!accountId && !!tenantId,
    });


    // Merge both transaction sources
    const allTransactions: UnifiedTransaction[] = [
        ...(bankingTxData || []),
        ...(salesTxData || []),
    ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    // Apply filters
    const filteredTransactions = allTransactions.filter(tx => {
        // Source filter
        if (sourceFilter) {
            if (sourceFilter === tx.source) return true;
            if (sourceFilter === tx.channel) return true;
            if (sourceFilter === 'sale' && (tx.source === 'sale' || tx.channel === 'bank_transfer' || tx.channel === 'pos_card' || tx.channel === 'cash' || tx.channel === 'mobile_money')) return true;
            return false;
        }
        // Search filter
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (
                (tx.description && tx.description.toLowerCase().includes(q)) ||
                (tx.source && tx.source.toLowerCase().includes(q)) ||
                (tx.reference && tx.reference.toLowerCase().includes(q)) ||
                (tx.saleOrderNumber && tx.saleOrderNumber.toLowerCase().includes(q)) ||
                tx.amount?.includes(q)
            );
        }
        return true;
    });

    // Pagination
    const totalCount = filteredTransactions.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const paginatedTransactions = filteredTransactions.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    // Stats from filtered transactions
    const credits = filteredTransactions.filter(t => t.type === 'credit');
    const debits = filteredTransactions.filter(t => t.type === 'debit');
    const totalCredits = credits.reduce((s, t) => s + parseFloat(t.amount || '0'), 0);
    const totalDebits = debits.reduce((s, t) => s + parseFloat(t.amount || '0'), 0);

    const isLoading = accountLoading || bankingTxLoading || salesTxLoading;

    // Loading state
    if (accountLoading) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <div className="text-center space-y-4">
                <Spinner size="xl" />
                <p className="text-sm text-slate-400 animate-pulse">Loading account details...</p>
            </div>
        </div>
    );

    // Error state
    if (accountError || !account) return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <ErrorState
                title="Account not found"
                message="This bank account may have been deleted or you don't have access to it."
                onBack={() => navigate('/bank-accounts')}
            />
        </div>
    );

    const cfg = accountTypeConfig[account.accountType] || accountTypeConfig.other;
    const Icon = cfg.icon;
    const balance = parseFloat(account.currentBalance || '0');
    const openingBalance = parseFloat(account.openingBalance || '0');
    const growth = openingBalance > 0 ? ((balance - openingBalance) / openingBalance) * 100 : 0;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
            <div className="max-w-[1400px] mx-auto p-4 lg:p-6 space-y-5">

                {/* Back Button */}
                <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    onClick={() => navigate('/bank-accounts')}
                    className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 group transition-colors">
                    <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Bank Accounts
                </motion.button>

                {/* ========== HERO CARD ========== */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`relative overflow-hidden rounded-3xl shadow-2xl bg-gradient-to-br ${cfg.heroGradient}`}>
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-white/30 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/20 rounded-full blur-3xl" />
                    </div>

                    <div className="relative px-6 lg:px-10 py-8 lg:py-10">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <motion.div
                                    whileHover={{ scale: 1.05, rotate: -3 }}
                                    className="p-4 rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20">
                                    <Icon size={32} className="text-white" />
                                </motion.div>
                                <div>
                                    <p className="text-white/50 text-sm font-medium uppercase tracking-widest">
                                        {cfg.label} Account
                                    </p>
                                    <h1 className="text-3xl lg:text-4xl font-black text-white tracking-tight">
                                        {account.bankName}
                                    </h1>
                                    <div className="flex items-center gap-3 mt-2 text-white/60 text-sm">
                                        <span className="font-mono tracking-wider">{account.accountNumber}</span>
                                        <span>·</span>
                                        <span>{account.accountName}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-white/40 text-xs uppercase tracking-widest font-bold">Current Balance</p>
                                <motion.p
                                    key={balance}
                                    initial={{ scale: 1.05 }}
                                    animate={{ scale: 1 }}
                                    className="text-4xl lg:text-5xl font-black text-white tracking-tight">
                                    {formatNaira(account.currentBalance)}
                                </motion.p>
                                <div className="flex items-center justify-end gap-2 mt-2">
                                    <span className={clsx('text-xs font-bold flex items-center gap-0.5',
                                        growth >= 0 ? 'text-emerald-300' : 'text-rose-300')}>
                                        {growth >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                        {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
                                    </span>
                                    <span className="text-white/40 text-xs">{account.currency || 'NGN'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Badges Row */}
                        <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/10 flex-wrap">
                            <Badge className="bg-white/10 text-white border-white/20 backdrop-blur-sm">
                                {account.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                            {account.isDefault && (
                                <Badge className="bg-amber-400/20 text-amber-200 border-amber-400/30 backdrop-blur-sm">
                                    <Award size={10} className="mr-1" />Default
                                </Badge>
                            )}
                            {account.branchName && (
                                <Badge className="bg-white/10 text-white border-white/20 backdrop-blur-sm">
                                    <Store size={10} className="mr-1" />{account.branchName}
                                </Badge>
                            )}
                            {account.businessName && (
                                <Badge className="bg-white/10 text-white border-white/20 backdrop-blur-sm">
                                    <Building2 size={10} className="mr-1" />{account.businessName}
                                </Badge>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* ========== STATS GRID ========== */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                        label="Total Credits"
                        value={formatNaira(totalCredits)}
                        icon={TrendingUp}
                        gradient="from-emerald-500 to-teal-600"
                        trend="up"
                    />
                    <StatCard
                        label="Total Debits"
                        value={formatNaira(totalDebits)}
                        icon={TrendingDown}
                        gradient="from-rose-500 to-pink-600"
                        trend="down"
                    />
                    <StatCard
                        label="Transactions"
                        value={totalCount}
                        icon={Activity}
                        gradient="from-blue-500 to-indigo-600"
                    />
                    <StatCard
                        label="Growth"
                        value={`${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`}
                        icon={Target}
                        gradient="from-purple-500 to-violet-600"
                        trend={growth >= 0 ? 'up' : 'down'}
                    />
                </div>

                {/* ========== TRANSACTIONS SECTION ========== */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    {/* Header with filters */}
                    <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                    <History size={18} className="text-blue-500" />
                                    Transaction History
                                </h3>
                                <p className="text-sm text-slate-500">
                                    {totalCount} transaction{totalCount !== 1 ? 's' : ''}
                                    {salesTxData && salesTxData.length > 0 && (
                                        <span className="text-emerald-500 ml-2">
                                            · {salesTxData.length} from sales
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        value={searchQuery}
                                        onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                        className="pl-8 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs w-36 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                    />
                                    {searchQuery && (
                                        <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                                            <X size={12} className="text-slate-400 hover:text-slate-600" />
                                        </button>
                                    )}
                                </div>
                                {isLoading && <Spinner size="xs" />}
                            </div>
                        </div>

                        {/* Filter Pills */}
                        <div className="flex gap-1.5 mt-3 flex-wrap">
                            {filterOptions.map(f => (
                                <button
                                    key={f.value}
                                    onClick={() => { setSourceFilter(f.value); setCurrentPage(1); }}
                                    className={clsx(
                                        'px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all',
                                        sourceFilter === f.value
                                            ? 'bg-slate-800 text-white shadow-sm'
                                            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                                    )}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Transactions List */}
                    <div className="max-h-[600px] overflow-y-auto">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-20">
                                <Spinner size="lg" />
                            </div>
                        ) : paginatedTransactions.length === 0 ? (
                            <div className="text-center py-20">
                                <History size={48} className="mx-auto mb-4 text-slate-300" />
                                <p className="text-sm font-medium text-slate-400">No transactions yet</p>
                                <p className="text-xs text-slate-400 mt-1">
                                    Transactions appear when sales, expenses, or adjustments are recorded.
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-50">
                                {paginatedTransactions.map((tx, i) => {
                                    const isCredit = tx.type === 'credit';
                                    const SourceIcon = getSourceIcon(tx.source);
                                    const displaySource = tx.channel
                                        ? getSourceLabel(tx.channel)
                                        : getSourceLabel(tx.source);

                                    return (
                                        <motion.div
                                            key={`${tx.sourceTable}-${tx.id}`}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: Math.min(i * 0.01, 0.3) }}
                                            className={clsx(
                                                'flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors group',
                                                tx.saleId && 'cursor-pointer'
                                            )}
                                            onClick={() => {
                                                if (tx.saleId) {
                                                    navigate(`/sales/${tx.saleId}`);
                                                }
                                            }}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={clsx(
                                                    'p-3 rounded-2xl transition-transform group-hover:scale-110',
                                                    isCredit ? 'bg-emerald-100' : 'bg-rose-100'
                                                )}>
                                                    {isCredit
                                                        ? <ArrowUpRight size={20} className="text-emerald-600" />
                                                        : <ArrowDownLeft size={20} className="text-rose-600" />
                                                    }
                                                </div>

                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <Badge
                                                            variant={isCredit ? 'success' : 'danger'}
                                                            size="sm"
                                                            className="capitalize"
                                                        >
                                                            {displaySource}
                                                        </Badge>
                                                        {tx.saleOrderNumber && (
                                                            <Badge variant="neutral" size="sm">
                                                                <Receipt size={10} className="mr-1" />
                                                                {tx.saleOrderNumber}
                                                            </Badge>
                                                        )}
                                                        {tx.sourceTable === 'sales' && (
                                                            <span className="text-[9px] text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">
                                                                Sale
                                                            </span>
                                                        )}
                                                    </div>
                                                    {tx.description && (
                                                        <p className="text-xs text-slate-500 mt-1">{tx.description}</p>
                                                    )}
                                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                        {tx.reference && (
                                                            <span className="text-[10px] text-slate-400 font-mono">
                                                                Ref: {tx.reference}
                                                            </span>
                                                        )}
                                                        <p className="text-[10px] text-slate-400">{formatDateTime(tx.createdAt)}</p>
                                                        <span className="text-[10px] text-slate-300">·</span>
                                                        <p className="text-[10px] text-slate-400">{formatTimeAgo(tx.createdAt)}</p>
                                                        {tx.status && tx.status !== 'confirmed' && (
                                                            <>
                                                                <span className="text-[10px] text-slate-300">·</span>
                                                                <Badge variant={tx.status === 'reversed' ? 'danger' : 'warning'} size="sm">
                                                                    {tx.status}
                                                                </Badge>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="text-right flex-shrink-0">
                                                <p className={clsx(
                                                    'text-sm font-black',
                                                    isCredit ? 'text-emerald-600' : 'text-rose-600'
                                                )}>
                                                    {isCredit ? '+' : '-'}{formatNaira(tx.amount)}
                                                </p>
                                                {tx.balanceAfter && (
                                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                                        Balance: {formatNaira(tx.balanceAfter)}
                                                    </p>
                                                )}
                                                {tx.saleId && (
                                                    <p className="text-[9px] text-blue-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-end gap-0.5">
                                                        <ExternalLink size={9} />
                                                        View Sale
                                                    </p>
                                                )}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                            <p className="text-xs text-slate-500">
                                Page {currentPage} of {totalPages} · {totalCount} transactions
                            </p>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft size={14} />
                                </button>

                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                    let pageNum: number;
                                    if (totalPages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={clsx(
                                                'w-8 h-8 rounded-lg text-xs font-medium transition-all',
                                                currentPage === pageNum
                                                    ? 'bg-slate-800 text-white shadow-md'
                                                    : 'text-slate-600 hover:bg-slate-200'
                                            )}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}

                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}